import { Menu } from 'lucide-react';
import type { ReactNode } from 'react';

import { useTranslation } from '@core/i18n';
import { IconButton } from '@core/ui';
import { cn } from '@core/utils/cn';

interface TopBarProps {
  onOpenNav: () => void;
  title?: string;
  actions?: ReactNode;
}

/**
 * Flat and anchored, not floating.
 *
 * The earlier version used the glass utility, which was the wrong call: glass is for
 * surfaces that sit ABOVE content -- the map detail panel, a sheet. A top bar is part of
 * the frame, so it gets a solid background and a hairline, and the page below scrolls
 * under nothing. Reserving glass for genuinely floating things is also what keeps it
 * meaningful when the fleet map uses it.
 */
export function TopBar({ onOpenNav, title, actions }: TopBarProps) {
  const { t } = useTranslation();
  return (
    <header
      className={cn(
        'border-separator bg-surface flex h-14 shrink-0 items-center gap-3 border-b px-3 sm:px-4',
      )}
    >
      <div className="lg:hidden">
        <IconButton
          label={t.nav.openMenu}
          icon={<Menu />}
          variant="ghost"
          size="icon-sm"
          onClick={onOpenNav}
        />
      </div>

      {/*
        A span, not an `h1`. This slot carries the product name, and every page already
        has its own `h1` in its `PageHeader` -- so marking the brand up as a heading gave
        each screen two competing document titles, and the one a screen reader reached
        first said "FleetTrace" on all of them. The brand is chrome; the page's own
        heading is the heading.
      */}
      {title && (
        <span className="truncate text-[15px] font-semibold tracking-[-0.01em]">{title}</span>
      )}

      {actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
    </header>
  );
}
